import React, { createContext, PropsWithChildren, useContext } from 'react';

import { ApolloError } from '@apollo/client';
import { ZoneId } from '@js-joda/core';
import { AddContextualData } from '@opendoor/observability';
import { getRailsEnv } from '@opendoor/shared-fe/railsEnv';

import {
  OdProtosSellCommon_DenialReason,
  OdProtosSellCommon_Product,
  OdProtosPartnershipData_ReferralInvitationAcceptanceStatus as ReferralAcceptanceStatus,
  OdProtosPartnershipData_Referral_AttributionType as ReferralAttributionType,
  OdProtosSellReceptionData_SellerInput_Channel as SellerInputChannel,
} from '__generated__/globalTypes';

import {
  CustomerAddressUUIDResponse,
  useCustomerAddressUUID,
} from 'platform/graphql/useCustomerAddressUUID';

import { useMarketData } from 'buying/graphql/useMarketData';

import { useIsExclusiveOffersQualified } from 'exclusives/graphql/useIsExclusiveOffersQualified';

import {
  PartnershipReferralResponse,
  usePartnershipReferrals,
} from 'growth/usePartnershipReferrals';

import { SellerLeadFragment as Lead } from 'sellDirect/graphql/fragments/__generated__/SellerLeadFragment';
import { SellerLeadResponse, useSellerLead } from 'sellDirect/graphql/SellerLeadContext';
import { useParticipants, UseParticipantsResult } from 'sellDirect/graphql/useParticipants';
import { getLeadInfo } from 'sellDirect/helpers/useCalendlyParams';

import { useBrowserCompatibilityData } from './globals';

const odRailsEnv = getRailsEnv();

/* Used for enabling buyer content for Redfin leads (if not blocked due to other conditions) */
const TEST_PARTNER_ORE_ID = 'test_partner_ore';
const RF_STAGING_ID = '31eaaf9e-5c26-4d2b-b402-43aba0369298';
const RF_PROD_ID = 'ce048db9-17f4-49c8-9ebf-2df9a6158fbd';

const BUYER_CONTENT_ELIGIBLE_ORE_PARTNER_IDENTIFIERS =
  odRailsEnv !== 'production'
    ? [TEST_PARTNER_ORE_ID, RF_STAGING_ID]
    : [TEST_PARTNER_ORE_ID, RF_PROD_ID];

export interface IQualificationContext
  extends Pick<CustomerAddressUUIDResponse, 'customerUUID' | 'addressUUID'>,
    Pick<
      SellerLeadResponse,
      'lead' | 'sellerInputUUID' | 'experimentEntityId' | 'customerLeads' | 'isBuyOnlyCustomer'
    > {
  /** ZoneId of the lead's address. */
  zoneId: ZoneId;
  refetchSellerLead?: SellerLeadResponse['refetch'];
  sellerLeadError?: ApolloError;
  loadingQualification: boolean;
  sellerLeadUUID?: string;
  isExclusiveOffersQualified: boolean;
  isSellDirectQualified: boolean;
  isREITQualified: boolean;
  isNonDirectOffer: boolean;
  inBwODMarket: boolean;
  blockBuyerContent: boolean;
  eligibleForBwODMessaging: boolean;
  agentReferralPartnerIdentifier: string | null;
  hasUnknownReferralAttributionType: boolean;
  isPendingAgentReferralAcceptance: boolean;
  refetchPartnershipReferrals?: PartnershipReferralResponse['refetch'];
  isPartnershipLead: boolean;
  participantsHelpers: UseParticipantsResult | undefined;
  refetchCustomerLeads?: CustomerAddressUUIDResponse['refetch'];
  partnershipReferralData?: PartnershipReferralResponse;
}

export const defaultLeadContext: IQualificationContext = {
  zoneId: ZoneId.systemDefault(),
  loadingQualification: false,
  sellerInputUUID: '',
  isExclusiveOffersQualified: false,
  isSellDirectQualified: false,
  isREITQualified: false,
  isNonDirectOffer: false,
  experimentEntityId: '',
  inBwODMarket: false,
  blockBuyerContent: false,
  eligibleForBwODMessaging: false,
  agentReferralPartnerIdentifier: null,
  hasUnknownReferralAttributionType: false,
  isPendingAgentReferralAcceptance: false,
  isPartnershipLead: false,
  participantsHelpers: undefined,
  customerUUID: '',
  customerLeads: [],
  addressUUID: undefined,
  isBuyOnlyCustomer: false,
};

export const QualificationContext = createContext<IQualificationContext>(defaultLeadContext);

/**
 * @throws error if qualification context is missing
 */
export const useQualificationContext = () => {
  const value = useContext(QualificationContext);
  if (!value) {
    throw new Error('QualificationContext missing');
  }
  return value;
};

/**
 * QualificationProvider is a wrapper component that provides the qualification context to its children.
 *
 * @throws error if qualification context is missing
 */
export const QualificationProvider: React.FC<
  PropsWithChildren<{
    contextOverride?: Partial<IQualificationContext>;
  }>
> = ({ contextOverride, children }) => {
  const { customerUUID, addressUUID, refetch: refetchCustomerLeads } = useCustomerAddressUUID();
  const {
    sellerInputUUID,
    lead,
    experimentEntityId,
    isBuyOnlyCustomer,
    customerLeads,
    ...sellerLeadQuery
  } = useSellerLead();

  const zoneId = lead?.address?.timeZone
    ? ZoneId.of(lead.address.timeZone)
    : ZoneId.systemDefault();

  // Get participant data + methods
  const participantsHelpers = useParticipants(sellerInputUUID);

  // Use addressUUID from query param if it exists
  const { loading: fetchingBWODMarket, inBwODMarket } = useMarketData(
    addressUUID || lead?.address?.uuid,
  );

  // Use sellerLeadUUID from query param if it exists
  const partnershipReferralData = usePartnershipReferrals(sellerInputUUID);
  const refetchPartnershipReferrals = partnershipReferralData.refetch;
  const agentReferralPartnerIdentifier =
    partnershipReferralData.agentReferral?.partnerIdentifier || null;

  // TODO: This is a temporary fix, we should bring consistency to referral data in partnership service
  //  such that all agent affiliated leads have a referral.
  const agentChannels = [
    SellerInputChannel.AGENT_REQUEST,
    SellerInputChannel.AGENT_REQUEST_LISTED,
    SellerInputChannel.SELLER_REQUEST_LISTED,
    SellerInputChannel.OUTBOUND_OFFERS_LISTED,
  ];
  const isAgentChannel = lead?.channel ? agentChannels.includes(lead?.channel) : false;

  const sellerLeadUUID = lead?.id ?? '';

  const isLwODQualified = isProductQualified(OdProtosSellCommon_Product.LIST_WITH_OPENDOOR, lead);
  const isSellDirectQualified = isProductQualified(
    OdProtosSellCommon_Product.OPENDOOR_DIRECT,
    lead,
  );
  const isREITQualified = isProductQualified(OdProtosSellCommon_Product.REIT, lead);

  // If the sell with excluisive product is qualified, and the sell direct lead is denied or the sell direct offer is in a state that
  // indicates that the users should still see the exclusive offers page, then we should consider the user qualified.
  const { isExclusiveOffersQualified, loading: loadingEo } = useIsExclusiveOffersQualified({
    sellerLead: lead,
  });

  const homeReserveEligible = isLwODQualified && !isSellDirectQualified;
  // Currently this logic duplicates homeReserveEligible for clarity
  // home reserve logic is changing so lwod standalone shouldn't
  // depend on that for now
  const isNonDirectOffer = isLwODQualified && !isSellDirectQualified;
  const { email, name, customerUUID: uuid } = getLeadInfo(lead);

  const showBuyerContentForOreLead = BUYER_CONTENT_ELIGIBLE_ORE_PARTNER_IDENTIFIERS.some((id) =>
    partnershipReferralData.partnershipIdentifiers?.includes(id),
  );

  const blockBuyerContent =
    partnershipReferralData.hasAgentReferral ||
    isAgentChannel ||
    (partnershipReferralData.hasOreReferral && !showBuyerContentForOreLead) ||
    partnershipReferralData.hasHomebuilderReferral;

  // There's some logic consolidation here compared to the EDD
  // as IMO it would be wise to not continue to proliferate
  // the reliance on Web lead channels AND partnership referral
  // data to make determine the specific partner experience a
  // user should get

  // Channel check unnecessary because we would have had updated the
  // attribution type if the agent opted for representation. If an ops
  // user manually flipped to lead from agent -> sell direct, the
  // agent referral would be deleted.
  // The referralInvitiationEligibility criterion takes into account
  // the listed status of the lead. Listed leads are not eligible.
  const hasUnknownReferralAttributionType =
    partnershipReferralData.agentReferral?.attributionType ===
      ReferralAttributionType.UNKNOWN_ATTRIBUTION &&
    partnershipReferralData.referralInvitiationEligibility;

  // Channel check unnecessary because the REFERRAL attribution type
  // indicates the agent has opted to refer. If flipping from sell direct
  // back to agent, we ensure that the attribution type is flipped to
  // REPRESENTATION
  const isPendingAgentReferralAcceptance =
    partnershipReferralData.agentReferral?.attributionType === ReferralAttributionType.REFERRAL &&
    partnershipReferralData.referralInvitation?.status === ReferralAcceptanceStatus.PENDING;

  const { isOpendoorAppWebView } = useBrowserCompatibilityData();
  const eligibleForBwODMessaging = inBwODMarket && !blockBuyerContent && !isOpendoorAppWebView;

  const contextualData = {
    sellerLeadUUID: lead?.id,
    sellerInputUUID,
    isMonitorTheMarket: isREITQualified,
    isSellerDash: isSellDirectQualified || isLwODQualified,
    isLwodOnly: isNonDirectOffer,
    homeReserveEligible,
    isNonDirectOffer,
    leadCustomer: {
      email,
      name,
      uuid,
    },
    market: lead?.address?.market?.toLowerCase(),
    isPartnershipLead: partnershipReferralData.isPartnershipLead,
    inBwODMarket,
    hasAgentReferral: partnershipReferralData.hasAgentReferral,
    isAgentChannel,
    blockBuyerContent,
    hasUnknownReferralAttributionType,
    isPendingAgentReferralAcceptance,
    customerUUID,
    addressUUID,
    isBuyOnlyCustomer,
    customerLeads: customerLeads?.length ?? 0,
  };

  const leadContext: React.ComponentProps<typeof AddContextualData>['leadContext'] = {
    sellerInputUuid: sellerInputUUID,
    sellerLeadUuid: sellerLeadUUID,
    sellerLeadMarket: lead?.address?.market?.toLowerCase(),
  };

  return (
    <QualificationContext.Provider
      value={{
        lead,
        zoneId,
        refetchSellerLead: sellerLeadQuery.refetch,
        sellerInputUUID,
        sellerLeadError: sellerLeadQuery.error,
        loadingQualification:
          sellerLeadQuery.loading ||
          fetchingBWODMarket ||
          partnershipReferralData.loading ||
          loadingEo,
        sellerLeadUUID,
        isExclusiveOffersQualified,
        isSellDirectQualified,
        isREITQualified,
        isPartnershipLead: partnershipReferralData.isPartnershipLead,
        isNonDirectOffer,
        experimentEntityId,
        inBwODMarket,
        blockBuyerContent,
        eligibleForBwODMessaging,
        agentReferralPartnerIdentifier,
        hasUnknownReferralAttributionType,
        isPendingAgentReferralAcceptance,
        refetchPartnershipReferrals,
        participantsHelpers,
        customerUUID,
        addressUUID,
        customerLeads,
        isBuyOnlyCustomer,
        refetchCustomerLeads,
        partnershipReferralData,
        ...contextOverride,
      }}
    >
      <AddContextualData data={contextualData} leadContext={leadContext}>
        {children}
      </AddContextualData>
    </QualificationContext.Provider>
  );
};

export const MockQualificationContext: React.FC<
  PropsWithChildren & Partial<IQualificationContext>
> = ({ children, ...props }) => (
  <QualificationContext.Provider value={{ ...defaultLeadContext, ...props }}>
    {children}
  </QualificationContext.Provider>
);

/**
 * Returns true if the lead is qualified for the given product.
 *
 * @param productOffering - The product to check qualification for.
 * @param lead - The lead to check qualification for.
 */
export const isProductQualified = (
  productOffering: OdProtosSellCommon_Product,
  lead?: Lead | null,
): boolean => {
  if (!lead || !lead.productOfferings || lead.productOfferings.length === 0) {
    return false;
  }

  const offering = lead.productOfferings.find(({ product }) => product === productOffering);
  return offering?.denialReason === OdProtosSellCommon_DenialReason.NO_DENIAL;
};

const SELLER_DASH_PRODUCTS: Array<OdProtosSellCommon_Product> = [
  OdProtosSellCommon_Product.OPENDOOR_DIRECT,
  OdProtosSellCommon_Product.LIST_WITH_OPENDOOR,
];

const getSellerDashProducts = (lead?: Lead | null) =>
  lead ? lead.productOfferings.filter(({ product }) => SELLER_DASH_PRODUCTS.includes(product)) : [];

/**
 * Returns true if all seller dash products are denied.
 *
 * @param lead
 */
export const deniedForAllSellerDashProducts = (lead?: Lead | null) =>
  getSellerDashProducts(lead).every(({ deniedAt }) => deniedAt);

/**
 * Returns true if any seller dash product is qualified.
 *
 * @param lead
 */
export const qualifiedForASellerDashProduct = (lead?: Lead | null) =>
  getSellerDashProducts(lead).some(
    ({ denialReason }) => denialReason === OdProtosSellCommon_DenialReason.NO_DENIAL,
  );
